/**
 * GrapesJS Canvas Improvements
 * Adds grid overlay, snap-to-grid, and canvas enhancements
 */

class CanvasGrid {
  constructor(editor) {
    this.editor = editor;
    this.gridSize = 10;
    this.snapEnabled = false; // Start disabled by default
    this.gridVisible = false; // Start hidden by default
    this.gridOverlay = null;
    this.init();
  }

  /**
   * Initialize grid system
   */
  init() {
    // Call immediately since editor might already be loaded
    const initGrid = () => {
      try {
        this.createGridOverlay();
        this.addSnapFunctionality();
        this.addControls();
      } catch (e) {
        // Canvas grid initialization failed silently
      }
    };

    // Try immediately first
    initGrid();

    // Also listen for load event in case it hasn't fired yet
    this.editor.on('load', initGrid);
  }

  /**
   * Create grid overlay element
   */
  createGridOverlay() {
    try {
      const canvasEl = this.editor.Canvas.getFrameEl();
      if (!canvasEl) {
        // Canvas frame not ready yet
        return;
      }

      const frameDoc = canvasEl.contentDocument || canvasEl.contentWindow.document;
      if (!frameDoc) {
        // Canvas document not accessible
        return;
      }

      const frameBody = frameDoc.body;
      if (!frameBody) {
        // Canvas body not found
        return;
      }

      // Create overlay container
      this.gridOverlay = frameDoc.createElement('div');
      if (!this.gridOverlay) {
        // Could not create grid overlay element
        return;
      }

      this.gridOverlay.className = 'gjs-grid-overlay';
      this.gridOverlay.innerHTML = this.generateGridSVG();
      this.gridOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1;
        display: none;
      `;

      frameBody.appendChild(this.gridOverlay);
    } catch (e) {
      // Grid overlay creation failed silently
    }
  }

  /**
   * Generate SVG grid pattern
   * @returns {string} SVG markup
   */
  generateGridSVG() {
    return `
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0;">
        <defs>
          <pattern id="grid-pattern" width="${this.gridSize}" height="${this.gridSize}" patternUnits="userSpaceOnUse">
            <path d="M ${this.gridSize} 0 L 0 0 0 ${this.gridSize}"
                  fill="none"
                  stroke="rgba(78, 93, 157, 0.15)"
                  stroke-width="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-pattern)" />
      </svg>
    `;
  }

  /**
   * Add snap-to-grid functionality
   */
  addSnapFunctionality() {
    // Listen for component position changes
    this.editor.on('component:update', (component) => {
      if (!this.snapEnabled) return;

      const styles = component.getStyle();

      // Only snap if component has explicit positioning
      if (styles.top || styles.left) {
        this.snapComponentToGrid(component);
      }
    });
  }

  /**
   * Snap a component to the grid
   * @param {Object} component - GrapesJS component
   */
  snapComponentToGrid(component) {
    const styles = component.getStyle();
    const newStyles = {};

    // Snap top position
    if (styles.top) {
      const topValue = parseFloat(styles.top);
      if (!isNaN(topValue)) {
        const snappedTop = Math.round(topValue / this.gridSize) * this.gridSize;
        newStyles.top = `${snappedTop}px`;
      }
    }

    // Snap left position
    if (styles.left) {
      const leftValue = parseFloat(styles.left);
      if (!isNaN(leftValue)) {
        const snappedLeft = Math.round(leftValue / this.gridSize) * this.gridSize;
        newStyles.left = `${snappedLeft}px`;
      }
    }

    // Apply snapped positions
    if (Object.keys(newStyles).length > 0) {
      component.setStyle(newStyles);
    }
  }

  /**
   * Toggle grid visibility
   */
  toggleGrid() {
    this.gridVisible = !this.gridVisible;

    if (this.gridOverlay) {
      this.gridOverlay.style.display = this.gridVisible ? 'block' : 'none';
    }

    // Update button state
    this.updateButtonState('grid-toggle', this.gridVisible);
  }

  /**
   * Toggle snap functionality
   */
  toggleSnap() {
    this.snapEnabled = !this.snapEnabled;

    // Update button state
    this.updateButtonState('snap-toggle', this.snapEnabled);
  }

  /**
   * Update toolbar button active state
   * @param {string} buttonId - Button ID
   * @param {boolean} active - Active state
   */
  updateButtonState(buttonId, active) {
    const panel = this.editor.Panels.getPanel('options');
    if (panel) {
      const button = panel.get('buttons').get(buttonId);
      if (button) {
        if (active) {
          button.set('active', true);
        } else {
          button.set('active', false);
        }
      }
    }
  }

  /**
   * Add grid and snap controls to toolbar
   */
  addControls() {
    // Try to find the options panel, or create it
    let panel = this.editor.Panels.getPanel('options');

    if (!panel) {
      // Try alternative panel names
      panel = this.editor.Panels.getPanel('views') ||
              this.editor.Panels.getPanel('devices-c');
    }

    if (!panel) {
      // Create the options panel if it doesn't exist
      panel = this.editor.Panels.addPanel({
        id: 'options',
        buttons: []
      });
    }

    if (!panel) {
      return; // Still no panel, give up silently
    }

    try {
      // Add grid toggle button
      panel.get('buttons').add([
        {
          id: 'grid-toggle',
          className: 'fa fa-th',
          command: () => this.toggleGrid(),
          attributes: {
            title: 'Toggle Grid (Shift+G)',
            'data-tooltip': 'Show/hide grid overlay'
          },
          active: false,
        },
        {
          id: 'snap-toggle',
          className: 'fa fa-magnet',
          command: () => this.toggleSnap(),
          attributes: {
            title: 'Toggle Snap to Grid (Shift+S)',
            'data-tooltip': 'Enable/disable snap to grid'
          },
          active: false,
        },
      ]);
    } catch (e) {
      // Failed to add buttons, fail silently
    }
  }

  /**
   * Change grid size
   * @param {number} size - New grid size in pixels
   */
  setGridSize(size) {
    this.gridSize = size;

    // Regenerate grid overlay with new size
    if (this.gridOverlay) {
      this.gridOverlay.innerHTML = this.generateGridSVG();
    }
  }

  /**
   * Get current grid size
   * @returns {number} Grid size in pixels
   */
  getGridSize() {
    return this.gridSize;
  }

  /**
   * Check if snap is enabled
   * @returns {boolean} Snap enabled state
   */
  isSnapEnabled() {
    return this.snapEnabled;
  }

  /**
   * Check if grid is visible
   * @returns {boolean} Grid visibility state
   */
  isGridVisible() {
    return this.gridVisible;
  }
}

/**
 * Responsive Canvas Manager
 * Handles device preview modes
 */
class ResponsiveCanvas {
  constructor(editor) {
    this.editor = editor;
    this.init();
  }

  /**
   * Initialize responsive canvas features
   */
  init() {
    try {
      this.addDeviceButtons();
      this.addDeviceChangeListener();
    } catch (e) {
      // Responsive canvas initialization failed silently
    }
  }

  /**
   * Add device preview buttons to toolbar
   */
  addDeviceButtons() {
    try {
      const panel = this.editor.Panels.getPanel('devices-c');

      if (!panel) {
        // Devices panel not available
        return;
      }

      // Device buttons are usually added by GrapesJS plugins
      // We just enhance them with better styling via CSS
    } catch (e) {
      // Device buttons initialization failed silently
    }
  }

  /**
   * Listen for device changes
   */
  addDeviceChangeListener() {
    const deviceManager = this.editor.DeviceManager;

    if (!deviceManager) {
      // Device manager not available
      return;
    }

    // Try to listen for device changes using the editor's event system
    try {
      this.editor.on('change:device', () => {
        const currentDevice = deviceManager.getSelected();
        const deviceName = currentDevice ? currentDevice.get('name') : 'Desktop';

        // Dispatch custom event
        this.dispatchDeviceChangeEvent(deviceName);
      });
    } catch (e) {
      // Device change listener failed silently
    }
  }

  /**
   * Dispatch custom event when device changes
   * @param {string} deviceName - Device name
   */
  dispatchDeviceChangeEvent(deviceName) {
    const event = new CustomEvent('grapesjs-device-change', {
      detail: { device: deviceName },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }
}

/**
 * Canvas Improvements Manager
 * Coordinates all canvas enhancements
 */
class CanvasImprovements {
  constructor(editor) {
    this.editor = editor;
    this.grid = null;
    this.responsive = null;
    this.init();
  }

  /**
   * Initialize all canvas improvements
   */
  init() {
    this.grid = new CanvasGrid(this.editor);
    this.responsive = new ResponsiveCanvas(this.editor);
  }

  /**
   * Get grid instance
   * @returns {CanvasGrid} Grid instance
   */
  getGrid() {
    return this.grid;
  }

  /**
   * Get responsive canvas instance
   * @returns {ResponsiveCanvas} Responsive canvas instance
   */
  getResponsive() {
    return this.responsive;
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CanvasImprovements;
} else {
  window.CanvasImprovements = CanvasImprovements;
}
